import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { getListingFullAddressString } from 'common/listingHelpers';
import { DISCOUNT_APPLY_GLOBAL } from 'common/financeConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY,
} from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE,
} from 'client/components/SrButton/SrButton';

import { createFormValidationFn, validateFormFieldsAndSubmit } from 'client/helpers/formikHelpers';

const getFormFields = ({ listings }) => {
  const discountOptions = [
    {
      value: DISCOUNT_APPLY_GLOBAL,
      label: 'Global Account Level',
    },
    ...(listings || []).map((listing) => ({
      value: listing.id,
      label: getListingFullAddressString(listing),
    })),
  ];

  return [
    {
      id: 'applyTo',
      type: 'select',
      title: 'Apply Discount',
      properties: {
        choices: discountOptions,
      },
      validations: ['required'],
    },
    {
      id: 'amount',
      type: 'currency',
      title: 'Discount Amount (in dollars)',
      properties: {
        placeholder: 'Write the discount amount in dollars.',
        upperLimit: 2000,
        lowerLimit: 1,
        prefix: '$',
      },
      validations: ['betweenNumbers', 'required'],
    },
    {
      id: 'partnerNote',
      title: 'Note for Partner',
      type: 'long_text',
      properties: {
        placeholder: 'Example: $100 discount applied for property setup issues.',
      },
      validations: ['required'],
    },
  ];
};

const SrBillingDiscountModal = ({ listings, onClose, onSubmit }) => {
  return (
    <Formik
      initialValues={{
        applyTo: DISCOUNT_APPLY_GLOBAL,
      }}
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={onSubmit}
    >
      {(formikBag) => {
        const formFields = getFormFields({ listings });
        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            contentMaxHeight="750px"
            contentPadding="28px 20px 0px 20px"
            buttonProps={[
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
              {
                text: 'Submit Discount as Credit',
                theme: SR_BUTTON_THEME_ADOBE_BLUE,
                onClick: () =>
                  validateFormFieldsAndSubmit({
                    formikBag,
                    formFields,
                  }),
              },
            ]}
          >
            <SrBox>
              <SrFlex marginTop="8px" marginBottom="36px" align="center" justify="center">
                <SrText font="greycliff" color="blueGreycliff" weight="800" fontSize="26px">
                  Create Discount
                </SrText>
              </SrFlex>
              <Form className="form">
                {formFields.map((formField) => (
                  <SrBox key={formField.id} marginBottom="16px">
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY}
                      margin={formField.margin}
                    />
                  </SrBox>
                ))}
              </Form>
            </SrBox>
          </SrModalV2>
        );
      }}
    </Formik>
  );
};

SrBillingDiscountModal.propTypes = {
  listings: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrBillingDiscountModal.displayName = 'SrBillingDiscountModal';

export default SrBillingDiscountModal;
